import type {
  DashboardData,
  TabData,
  DashboardFilters,
  ChatMessage,
  ChatContext,
  ChatAction,
  ChatSuggestion,
  ChatFeedback,
  DailyReport,
  DailyReportAlert,
} from '@/types/dashboard';

export interface PaginationOptions {
  offset?: number;
  limit?: number;
  sortCol?: string;
  sortOrder?: 'asc' | 'desc';
}

/** 按块「加载更多」的响应：该块当前条件下的总行数 + 指定 offset 区间的行 */
export interface BlockRowsResponse {
  rows: Record<string, unknown>[];
  total: number;
}

export interface DashboardService {
  fetchDashboard(signal?: AbortSignal): Promise<DashboardData>;
  fetchTab(
    tabId: string,
    filters?: DashboardFilters,
    pagination?: PaginationOptions,
    signal?: AbortSignal,
  ): Promise<TabData>;
  fetchBlock(
    tabId: string,
    block: string,
    filters?: DashboardFilters,
    pagination?: PaginationOptions,
    exact?: boolean,
    signal?: AbortSignal,
  ): Promise<BlockRowsResponse>;
  /**
   * 筛选候选值（字段 → 枚举值列表），用于下拉点选筛选。
   * 不传 tabId 时返回跨 tab 全局候选值；传入 tabId 时返回该 tab 的候选值。
   */
  fetchFilterOptions(tabId?: string, signal?: AbortSignal): Promise<Record<string, string[]>>;
  /**
   * 按关键字远程搜索单个维度的候选值（用于客户/人员等高基数字段）。
   * 返回匹配前 N 个枚举值（默认 50）。
   */
  searchFilterOptions(field: string, keyword: string, signal?: AbortSignal): Promise<string[]>;
  sendChatMessage(
    message: string,
    context?: ChatContext,
    history?: ChatMessage[],
    onStream?: (event: {
      type: 'start' | 'thinking' | 'content' | 'action' | 'suggestions' | 'daily_report' | 'done' | 'error';
      text?: string;
      steps?: string[];
      action?: ChatAction;
      suggestions?: ChatSuggestion[];
      report?: DailyReport;
      messageId?: string;
      error?: string;
    }) => void,
    signal?: AbortSignal,
  ): Promise<ChatMessage>;
  /** 提交对助手回答的反馈 */
  sendFeedback(feedback: ChatFeedback): Promise<{ success: boolean }>;
  /** 生成经营日报（支持流式回调） */
  generateDailyReport(onStream?: (event: {
    type: 'start' | 'thinking' | 'daily_report' | 'done' | 'error';
    report?: DailyReport;
    text?: string;
    steps?: string[];
    error?: string;
  }) => void, signal?: AbortSignal): Promise<DailyReport | null>;
  /** 获取当前主动预警异常列表 */
  fetchAlerts(): Promise<{ alerts: DailyReportAlert[]; count: number }>;
  /** 截图识别提问 */
  sendVisionQuestion(imageBase64: string, context?: ChatContext): Promise<ChatMessage>;
}

const DATA_MODE = import.meta.env.VITE_DATA_MODE || 'local';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class LocalDashboardService implements DashboardService {
  private meta: DashboardData | null = null;

  async fetchDashboard(_signal?: AbortSignal): Promise<DashboardData> {
    if (this.meta) return this.meta;
    const response = await fetch('/tabs/meta.json');
    if (!response.ok) {
      throw new Error(`Failed to load local dashboard meta: ${response.status}`);
    }
    this.meta = (await response.json()) as DashboardData;
    return this.meta;
  }

  async fetchTab(
    tabId: string,
    _filters?: DashboardFilters,
    _pagination?: PaginationOptions,
    _signal?: AbortSignal,
  ): Promise<TabData> {
    const safeName = encodeURIComponent(tabId.replace(/\//g, '_'));
    const response = await fetch(`/tabs/${safeName}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load local tab data: ${tabId} (${response.status})`);
    }
    return (await response.json()) as TabData;
  }

  async fetchBlock(
    _tabId: string,
    _block: string,
    _filters?: DashboardFilters,
    _pagination?: PaginationOptions,
    _exact?: boolean,
    _signal?: AbortSignal,
  ): Promise<BlockRowsResponse> {
    // 本地静态 JSON 无服务端分页，明细不会被截断，不会触发按块加载
    return { rows: [], total: 0 };
  }

  async fetchFilterOptions(_tabId?: string, _signal?: AbortSignal): Promise<Record<string, string[]>> {
    // 本地静态 JSON 无派生维度，无候选值（筛选面板此时仅显示字段）
    return {};
  }

  async searchFilterOptions(_field: string, _keyword: string, _signal?: AbortSignal): Promise<string[]> {
    // 本地静态 JSON 不支持远程搜索
    return [];
  }

  async sendChatMessage(
    message: string,
    _context?: ChatContext,
    _history?: ChatMessage[],
    onStream?: (event: {
      type: 'start' | 'thinking' | 'content' | 'action' | 'suggestions' | 'daily_report' | 'done' | 'error';
      text?: string;
      steps?: string[];
      action?: ChatAction;
      suggestions?: ChatSuggestion[];
      report?: DailyReport;
      messageId?: string;
      error?: string;
    }) => void,
    _signal?: AbortSignal,
  ): Promise<ChatMessage> {
    const content = `（本地模式）当前客服仅作演示。你问的是：${message}\n\n切换 VITE_DATA_MODE=api 后可调用后端 AI 客服。`;
    if (onStream) {
      onStream({ type: 'start' });
      onStream({ type: 'thinking', text: '正在理解您的问题…', steps: ['正在理解您的问题…'] });
      onStream({ type: 'content', text: content });
      onStream({ type: 'done' });
    }
    return { role: 'assistant', content };
  }

  async sendFeedback(): Promise<{ success: boolean }> {
    return { success: true };
  }

  async generateDailyReport(
    onStream?: (event: {
      type: 'start' | 'thinking' | 'daily_report' | 'done' | 'error';
      report?: DailyReport;
      text?: string;
      steps?: string[];
      error?: string;
    }) => void,
    _signal?: AbortSignal,
  ): Promise<DailyReport | null> {
    const report: DailyReport = {
      title: '经营日报（本地演示）',
      period: '本地模式',
      generatedAt: new Date().toISOString(),
      summary: '（本地模式）日报功能需切换 VITE_DATA_MODE=api 后使用。',
      sections: [],
      alerts: [],
    };
    if (onStream) {
      onStream({ type: 'start' });
      onStream({ type: 'thinking', text: '正在汇总经营日报…', steps: ['正在汇总经营日报…'] });
      onStream({ type: 'daily_report', report });
      onStream({ type: 'done' });
    }
    return report;
  }

  async fetchAlerts(): Promise<{ alerts: DailyReportAlert[]; count: number }> {
    return { alerts: [], count: 0 };
  }

  async sendVisionQuestion(): Promise<ChatMessage> {
    return { role: 'assistant', content: '（本地模式）截图识别需切换 VITE_DATA_MODE=api 后使用。' };
  }
}

class ApiDashboardService implements DashboardService {
  /** 筛选候选值按 tab 缓存，避免用户反复打开下拉时重复请求 */
  private filterOptionsCache = new Map<string, Record<string, string[]>>();

  private async api<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message = body?.error || body?.detail || `API error: ${response.status}`;
      // 503 且后端标记 DB_BUSY：让上层知道可以刷新/重试
      if (response.status === 503 || body?.code === 'DB_BUSY') {
        throw new Error(`[DB_BUSY] ${message}`);
      }
      throw new Error(message);
    }

    return response.json() as Promise<T>;
  }

  async fetchDashboard(signal?: AbortSignal): Promise<DashboardData> {
    return this.api<DashboardData>('/dashboard', { signal });
  }

  private buildQuery(
    filters?: DashboardFilters,
    pagination?: PaginationOptions,
    block?: string,
    exact?: boolean,
  ): string {
    const params = new URLSearchParams();
    if (pagination?.offset !== undefined) params.append('offset', String(pagination.offset));
    if (pagination?.limit !== undefined) params.append('limit', String(pagination.limit));
    if (pagination?.sortCol) params.append('sort_col', pagination.sortCol);
    if (pagination?.sortOrder) params.append('sort_order', pagination.sortOrder);
    if (block) params.append('block', block);
    if (exact) params.append('exact', '1');
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }
    return params.toString() ? `?${params.toString()}` : '';
  }

  async fetchTab(
    tabId: string,
    filters?: DashboardFilters,
    pagination?: PaginationOptions,
    signal?: AbortSignal,
  ): Promise<TabData> {
    return this.api<TabData>(`/dashboard/${encodeURIComponent(tabId)}${this.buildQuery(filters, pagination)}`, {
      signal,
    });
  }

  async fetchBlock(
    tabId: string,
    block: string,
    filters?: DashboardFilters,
    pagination?: PaginationOptions,
    exact?: boolean,
    signal?: AbortSignal,
  ): Promise<BlockRowsResponse> {
    // 后端 /dashboard/:tabId?block= 返回完整 TabData（ drills[0] 即目标块）
    const data = await this.api<TabData>(
      `/dashboard/${encodeURIComponent(tabId)}${this.buildQuery(filters, pagination, block, exact)}`,
      { signal },
    );
    const target = data.drills?.[0];
    if (target) {
      return { rows: target.data, total: target.total ?? target.data.length };
    }
    // 异常块理论上也会包装在 drills[0] 返回，此处兜底
    return { rows: [], total: 0 };
  }

  async fetchFilterOptions(tabId?: string, signal?: AbortSignal): Promise<Record<string, string[]>> {
    const cacheKey = tabId ?? '__global__';
    const cached = this.filterOptionsCache.get(cacheKey);
    if (cached) return cached;

    const path = tabId
      ? `/dashboard/${encodeURIComponent(tabId)}/filter-options`
      : '/dashboard/filter-options';
    const res = await this.api<{ options: Record<string, string[]> }>(path, { signal });
    const options = res.options || {};
    this.filterOptionsCache.set(cacheKey, options);
    return options;
  }

  /** 数据刷新后可调用此方法清空筛选候选值缓存 */
  clearFilterOptionsCache(): void {
    this.filterOptionsCache.clear();
  }

  async searchFilterOptions(field: string, keyword: string, signal?: AbortSignal): Promise<string[]> {
    const res = await this.api<{ options: string[] }>(
      `/dashboard/filter-search?field=${encodeURIComponent(field)}&keyword=${encodeURIComponent(keyword)}&limit=50`,
      { signal },
    );
    return res.options || [];
  }

  async sendChatMessage(
    message: string,
    context?: ChatContext,
    history?: ChatMessage[],
    onStream?: (event: {
      type: 'start' | 'thinking' | 'content' | 'action' | 'suggestions' | 'daily_report' | 'done' | 'error';
      text?: string;
      steps?: string[];
      action?: ChatAction;
      suggestions?: ChatSuggestion[];
      report?: DailyReport;
      messageId?: string;
      error?: string;
    }) => void,
    signal?: AbortSignal,
  ): Promise<ChatMessage> {
    // 无流式回调时走原来的 JSON 路径
    if (!onStream) {
      return this.api<ChatMessage>('/chat', {
        method: 'POST',
        body: JSON.stringify({ message, context, history }),
        signal,
      });
    }

    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, context, history, stream: true }),
      signal,
    });

    if (!response.ok || !response.body) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || body?.detail || `流式请求失败：${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let finalAction: ChatAction | undefined;
    let finalSuggestions: ChatSuggestion[] | undefined;
    let finalMessageId: string | undefined;

    onStream({ type: 'start' });

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventText of events) {
          const lines = eventText.split('\n');
          let event = '';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7);
            if (line.startsWith('data: ')) dataStr = line.slice(6);
          }
          if (!event || !dataStr) continue;

          let data: Record<string, unknown>;
          try {
            data = JSON.parse(dataStr) as Record<string, unknown>;
          } catch {
            console.warn('SSE parse error:', dataStr);
            continue;
          }
          if (event === 'content') {
            fullText += (data.text as string) || '';
            onStream({ type: 'content', text: fullText });
          } else if (event === 'thinking') {
            onStream({ type: 'thinking', text: (data.text as string) || '', steps: (data.steps as string[]) || [] });
          } else if (event === 'action') {
            finalAction = data as unknown as ChatAction;
            onStream({ type: 'action', action: finalAction });
          } else if (event === 'suggestions') {
            finalSuggestions = (data.suggestions as ChatSuggestion[]) || [];
            onStream({ type: 'suggestions', suggestions: finalSuggestions });
          } else if (event === 'daily_report') {
            const report = data.report as DailyReport;
            finalAction = { type: 'show_daily_report', report };
            onStream({ type: 'daily_report', report });
          } else if (event === 'done') {
            finalMessageId = (data.messageId as string) || finalMessageId;
            onStream({ type: 'done', messageId: finalMessageId });
          } else if (event === 'error') {
            onStream({ type: 'error', error: (data.message as string) || '未知错误' });
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw err;
      }
      console.error('SSE read error:', err);
      onStream({ type: 'error', error: err instanceof Error ? err.message : '流式读取失败' });
    }

    return {
      role: 'assistant',
      content: fullText,
      messageId: finalMessageId,
      ...(finalAction ? { action: finalAction } : {}),
      ...(finalSuggestions && finalSuggestions.length > 0 ? { suggestions: finalSuggestions } : {}),
    };
  }

  async sendFeedback(feedback: ChatFeedback): Promise<{ success: boolean }> {
    return this.api<{ success: boolean }>('/chat/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  }

  async generateDailyReport(
    onStream?: (event: {
      type: 'start' | 'thinking' | 'daily_report' | 'done' | 'error';
      report?: DailyReport;
      text?: string;
      steps?: string[];
      error?: string;
    }) => void,
    signal?: AbortSignal,
  ): Promise<DailyReport | null> {
    if (!onStream) {
      const res = await this.api<{ report: DailyReport }>('/chat/daily-report', {
        method: 'POST',
        body: JSON.stringify({ stream: false }),
        signal,
      });
      return res.report || null;
    }

    const response = await fetch(`${API_BASE_URL}/chat/daily-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stream: true }),
      signal,
    });

    if (!response.ok || !response.body) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || body?.detail || `日报流式请求失败：${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalReport: DailyReport | null = null;

    onStream({ type: 'start' });

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventText of events) {
          const lines = eventText.split('\n');
          let event = '';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7);
            if (line.startsWith('data: ')) dataStr = line.slice(6);
          }
          if (!event || !dataStr) continue;

          let data: Record<string, unknown>;
          try {
            data = JSON.parse(dataStr) as Record<string, unknown>;
          } catch {
            console.warn('daily report SSE parse error:', dataStr);
            continue;
          }
          if (event === 'thinking') {
            onStream({ type: 'thinking', text: (data.text as string) || '', steps: (data.steps as string[]) || [] });
          } else if (event === 'daily_report') {
            finalReport = data.report as DailyReport;
            onStream({ type: 'daily_report', report: finalReport });
          } else if (event === 'done') {
            onStream({ type: 'done' });
          } else if (event === 'error') {
            onStream({ type: 'error', error: (data.message as string) || '未知错误' });
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw err;
      }
      console.error('daily report SSE read error:', err);
      onStream({ type: 'error', error: err instanceof Error ? err.message : '日报流式读取失败' });
    }

    return finalReport;
  }

  async fetchAlerts(): Promise<{ alerts: DailyReportAlert[]; count: number }> {
    return this.api<{ alerts: DailyReportAlert[]; count: number }>('/chat/alerts', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async sendVisionQuestion(imageBase64: string, context?: ChatContext): Promise<ChatMessage> {
    return this.api<ChatMessage>('/chat/vision', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64, context }),
    });
  }
}

export function createDashboardService(): DashboardService {
  if (DATA_MODE === 'api') {
    return new ApiDashboardService();
  }
  return new LocalDashboardService();
}

export const dashboardService = createDashboardService();
